import { Example } from '@/types/example';

export const examples: Example[] = [
  {
    id: 'saas-startup-pitch',
    title: 'SaaS Startup Pitch - Series A',
    description: 'A comprehensive Series A funding pitch for an AI analytics SaaS platform. Showcases problem-solution fit, market opportunity, traction metrics, and team credentials.',
    story: 'DataFlow AI is raising $5M Series A to expand their AI-powered analytics platform. This pitch deck helped them secure meetings with top-tier VCs.',
    industry: 'technology',
    useCase: 'startup-pitch',
    complexity: 'advanced',
    slideCount: 14,
    thumbnail: '/examples/thumbnails/saas-startup-pitch.jpg',
    agents: ['director', 'scripter', 'graphic-artist', 'data-visualizer'],
    featured: true,
    tags: ['startup', 'fundraising', 'series-a', 'saas', 'ai'],
    createdAt: '2025-01-20',
    viewCount: 1250,
  },
  {
    id: 'q4-marketing-results',
    title: 'Q4 Marketing Campaign Results',
    description: 'Quarterly marketing performance review for an e-commerce company. Includes campaign metrics, channel analysis, ROI breakdown, and Q1 strategy recommendations.',
    story: 'An e-commerce brand presenting Q4 holiday campaign results to stakeholders. Shows 145% increase in ROAS and successful influencer partnerships.',
    industry: 'marketing',
    useCase: 'business-reporting',
    complexity: 'intermediate',
    slideCount: 12,
    thumbnail: '/examples/thumbnails/q4-marketing-results.jpg',
    agents: ['director', 'scripter', 'data-visualizer'],
    featured: true,
    tags: ['marketing', 'quarterly', 'metrics', 'roi', 'ecommerce'],
    createdAt: '2025-01-18',
    viewCount: 980,
  },
  {
    id: 'product-roadmap-2025',
    title: 'Product Roadmap 2025',
    description: 'Strategic product roadmap for a mobile app startup. Features timeline visualization, feature prioritization framework, and resource allocation plan.',
    story: 'A fintech mobile app presenting their 2025 roadmap to investors and team. Highlights AI integration, multi-currency support, and partnership expansion.',
    industry: 'technology',
    useCase: 'strategy',
    complexity: 'intermediate',
    slideCount: 16,
    thumbnail: '/examples/thumbnails/product-roadmap-2025.jpg',
    agents: ['director', 'scripter', 'graphic-artist', 'data-visualizer'],
    tags: ['product', 'roadmap', 'strategy', 'planning', 'fintech'],
    createdAt: '2025-01-15',
    viewCount: 750,
  },
  {
    id: 'sales-training-deck',
    title: 'Sales Training: Objection Handling',
    description: 'Comprehensive sales training module focused on handling common objections. Includes role-play scenarios, response frameworks, and real customer examples.',
    story: 'A B2B SaaS company onboarding new sales reps with proven objection handling techniques that increased close rates by 35%.',
    industry: 'sales',
    useCase: 'training',
    complexity: 'basic',
    slideCount: 10,
    thumbnail: '/examples/thumbnails/sales-training-deck.jpg',
    agents: ['director', 'scripter', 'graphic-artist'],
    tags: ['sales', 'training', 'onboarding', 'b2b', 'objections'],
    createdAt: '2025-01-12',
    viewCount: 620,
  },
  {
    id: 'ai-lecture-university',
    title: 'Introduction to AI - University Lecture',
    description: 'Academic lecture covering AI fundamentals, machine learning basics, neural networks, and ethical considerations. Designed for computer science undergraduates.',
    story: 'Stanford CS course introducing AI concepts with clear visualizations and real-world applications. Used by 200+ students in Spring 2025 semester.',
    industry: 'education',
    useCase: 'academic',
    complexity: 'intermediate',
    slideCount: 18,
    thumbnail: '/examples/thumbnails/ai-lecture-university.jpg',
    agents: ['scripter', 'graphic-artist', 'data-visualizer'],
    featured: true,
    tags: ['education', 'ai', 'machine-learning', 'university', 'lecture'],
    createdAt: '2025-01-10',
    viewCount: 1100,
  },
  {
    id: 'creative-agency-portfolio',
    title: 'Creative Agency Portfolio 2025',
    description: 'Stunning portfolio showcasing award-winning creative work. Features case studies, client testimonials, team capabilities, and creative process.',
    story: 'A digital creative agency pitching to Fortune 500 clients. Portfolio led to 3 new retainer contracts worth $500K+ annually.',
    industry: 'creative',
    useCase: 'portfolio',
    complexity: 'advanced',
    slideCount: 12,
    thumbnail: '/examples/thumbnails/creative-agency-portfolio.jpg',
    agents: ['director', 'graphic-artist'],
    tags: ['portfolio', 'creative', 'design', 'agency', 'branding'],
    createdAt: '2025-01-08',
    viewCount: 890,
  },
  {
    id: 'data-science-q3-report',
    title: 'Data Science Team Q3 Report',
    description: 'Comprehensive data science quarterly report featuring ML model performance, A/B test results, predictive analytics insights, and infrastructure improvements.',
    story: 'A tech company\'s data science team presenting Q3 achievements. Highlights 23% improvement in recommendation accuracy and successful deployment of 4 new models.',
    industry: 'analytics',
    useCase: 'data-reporting',
    complexity: 'advanced',
    slideCount: 15,
    thumbnail: '/examples/thumbnails/data-science-q3-report.jpg',
    agents: ['director', 'scripter', 'data-visualizer'],
    tags: ['data-science', 'analytics', 'ml', 'reporting', 'quarterly'],
    createdAt: '2025-01-05',
    viewCount: 540,
  },
  {
    id: 'non-profit-fundraising',
    title: 'Environmental Non-Profit Fundraising',
    description: 'Compelling fundraising pitch for ocean conservation non-profit. Showcases impact metrics, success stories, program expansion plans, and donation options.',
    story: 'Ocean Guardians presenting to potential major donors. Pitch resulted in $2.3M in pledges to fund marine habitat restoration programs.',
    industry: 'non-profit',
    useCase: 'fundraising',
    complexity: 'intermediate',
    slideCount: 10,
    thumbnail: '/examples/thumbnails/non-profit-fundraising.jpg',
    agents: ['director', 'scripter', 'graphic-artist'],
    tags: ['non-profit', 'fundraising', 'environmental', 'ocean', 'conservation'],
    createdAt: '2025-01-03',
    viewCount: 480,
  },
];

export const exampleIndustries = [
  { value: 'technology', label: 'Technology', count: examples.filter(e => e.industry === 'technology').length },
  { value: 'marketing', label: 'Marketing', count: examples.filter(e => e.industry === 'marketing').length },
  { value: 'sales', label: 'Sales', count: examples.filter(e => e.industry === 'sales').length },
  { value: 'education', label: 'Education', count: examples.filter(e => e.industry === 'education').length },
  { value: 'analytics', label: 'Analytics', count: examples.filter(e => e.industry === 'analytics').length },
  { value: 'creative', label: 'Creative', count: examples.filter(e => e.industry === 'creative').length },
  { value: 'non-profit', label: 'Non-Profit', count: examples.filter(e => e.industry === 'non-profit').length },
];

export const exampleUseCases = [
  { value: 'startup-pitch', label: 'Startup Pitch' },
  { value: 'business-reporting', label: 'Business Reporting' },
  { value: 'strategy', label: 'Strategy' },
  { value: 'training', label: 'Training' },
  { value: 'academic', label: 'Academic' },
  { value: 'portfolio', label: 'Portfolio' },
  { value: 'data-reporting', label: 'Data Reporting' },
  { value: 'fundraising', label: 'Fundraising' },
];
